import { useCallback } from "react";
import { useTranslation } from "@ncp/i18n";

export function humanise(key: string): string {
  const words = key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_.-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** i18next reads dots as nesting, and connector tools carry them in their keys (`fcsc-statistics.get_series`). */
const labelKey = (tool: string) => tool.replace(/[^A-Za-z0-9_]/g, "_");

/**
 * What a tool step did, in the reader's language: `ai.tools.<tool>` once done and `ai.toolsActive.<tool>` while it runs, falling back to
 * the tool's key in words for tools without a label (connector operations, new tools).
 */
export function useToolLabel(): (tool: string, active?: boolean) => string {
  const { t } = useTranslation();
  return useCallback((tool: string, active = false) => {
    const key = labelKey(tool);
    const done = t(`ai.tools.${key}`, { defaultValue: humanise(tool) });
    return active ? t(`ai.toolsActive.${key}`, { defaultValue: done }) : done;
  }, [t]);
}

/** A tool's own summary reads well ("12 passages from 4 papers") unless it is raw arguments or identifiers, which only the debug view shows. */
export function readableDetail(summary?: string | null): string | null {
  const s = summary?.trim();
  if (!s || /[{}[\]=<>|]|\b[a-z]+_[a-z_]+\b|^[\w.-]+:[\w.:-]+$/.test(s)) return null;
  return s.length > 160 ? `${s.slice(0, 157)}…` : s;
}
